/**
 * Atlas Iframe Service
 *
 * Handles communication between the Vue.js parent application and the
 * React/Next.js Atlas frontend embedded via iframe.
 */

import { getFreshIdentityToken } from '@/services/http';
import { isAllowedExternalRedirect } from '@/utils/externalRedirect';

// Message types sent from parent (Vue) to child (React)
export type ParentMessageType = 'AUTH_TOKEN' | 'NAVIGATE' | 'THEME_SYNC' | 'CONTEXT_UPDATE' | 'OPEN_LIVE_CHAT_RESULT' | 'LIVE_CHAT_STATE' | 'SHOW_ATLAS';

// Message types received from child (React) to parent (Vue)
export type ChildMessageType = 'READY' | 'REQUEST_AUTH' | 'NAVIGATION' | 'ACTION_COMPLETE' | 'RESIZE' | 'PARENT_NAVIGATE' | 'PRINT' | 'QUERY_PARAMS' | 'PURCHASE_LIST_CHANGED' | 'NAVIGATE_ACK' | 'OPEN_LIVE_CHAT' | 'REOPEN_LIVE_CHAT' | 'PARENT_REDIRECT_EXTERNAL';

export interface ParentMessage {
  type: ParentMessageType;
  payload: unknown;
  source: 'warbler-parent';
}

export interface ChildMessage {
  type: ChildMessageType;
  payload: unknown;
  source: 'atlas-frontend';
}

/** Why Atlas handed the merchant to a person (AS-11647 P0-2). Lands in Crisp
 *  `session:data` as `handoff_intent`, so the operator knows before reading. */
export type HandoffIntent = 'human_requested' | 'engineering_needed' | 'policy_topic' | 'ai_stuck';
const HANDOFF_INTENTS: readonly string[] = ['human_requested', 'engineering_needed', 'policy_topic', 'ai_stuck'];

export interface OpenLiveChatPayload {
  ticketKey?: string;
  reason?: string;
  shop?: string;
  /** First message to send into the chat — the downgrade funnel's feedback
   *  (AS-10883). Absent for an ordinary support handoff. */
  message?: string;
  /** AS-11647 P0-2. Only the four known values pass; anything else is dropped. */
  handoffIntent?: HandoffIntent;
  /** Atlas's 2–3 sentence summary of the issue, sent into the conversation (P0-2). */
  summary?: string;
  /** Echoed back on OPEN_LIVE_CHAT_RESULT so Atlas can match the answer to its
   *  request (P0-4). An older Atlas sends none and simply gets no answer it
   *  waits for. */
  requestId?: string;
}

/** What warbler's Crisp widget is doing, mirrored into Atlas (AS-11647 P0-5). */
export interface LiveChatState {
  /** The chatbox is open right now. */
  open: boolean;
  /** The support team wrote while the chatbox was closed. */
  unread: boolean;
  /** A handoff from Atlas is open this session: Atlas may offer the way back. */
  handoff: boolean;
}

export interface AtlasIframeConfig {
  /** The base URL of the Atlas frontend */
  baseUrl: string;
  /** Callback when iframe is ready */
  onReady?: () => void;
  /** Callback when auth is requested */
  onAuthRequest?: () => void;
  /** Callback when navigation changes in iframe */
  onNavigation?: (route: string) => void;
  /** Callback when an action completes */
  onActionComplete?: (action: string, result: unknown) => void;
  /** Callback when resize is requested */
  onResize?: (height: number) => void;
  /** Callback when Atlas requests navigation in the parent app.
   *  `switchToClassic` is true ONLY for the explicit "Switch back to classic
   *  view" links (Atlas ClassicSwitchBanner) — ordinary navigations must NOT
   *  flip a feature's atlas/classic version (see AtlasEmbed's handler). */
  onParentNavigate?: (
    path: string,
    switchToClassic?: boolean,
    opts?: { reload?: boolean },
  ) => void;
  /** Callback when Atlas requests printing */
  onPrint?: (html: string, title: string) => void;
  /** Callback when Atlas updates its URL query parameters */
  onQueryParams?: (queryString: string) => void;
  /** Callback when Atlas's purchase list changes (item added/removed/updated/cleared) */
  onPurchaseListChanged?: () => void;
  /** Callback when Atlas acknowledges a NAVIGATE (soft navigation accepted).
   *  Absence of this within the deadline is what triggers the hard-reload
   *  fallback — see AtlasSoftNavController. */
  onNavigateAck?: (path: string) => void;
  /** Callback when Atlas requests the parent open Crisp live chat (agent handoff).
   *  `shop` may be undefined — Atlas's useShopName() can resolve it after the
   *  first render, so a handoff fired before then omits it. */
  onOpenLiveChat?: (payload: OpenLiveChatPayload) => void;
  /** Callback when Atlas asks to bring back a handoff conversation the merchant
   *  minimised (AS-11647 P0-5). Shows Crisp; sets no session data. */
  onReopenLiveChat?: () => void;
  /** Callback when Atlas asks the parent to leave the app for an external
   *  Shopify URL — in practice the charge-approval `confirmationUrl`.
   *
   *  Atlas cannot navigate the top-level window itself, so billing routes the
   *  redirect through here and warbler performs it with App Bridge. The URL is
   *  ALREADY validated against the Shopify allowlist when this fires; the
   *  handler below drops anything else, so implementers do not have to
   *  re-check and cannot forget to. See AS-10881. */
  onRedirectExternal?: (url: string) => void;
}

type MessageHandler<T = unknown> = (payload: T) => void;

/**
 * The service instance currently bound to a live iframe.
 *
 * AtlasEmbed builds its service with createAtlasIframeService(), so the
 * exported singleton below is never the attached one — anything that needs to
 * reach the real iframe from outside the component (the identity-token refresh
 * hook in http.ts) has to ask for the attached instance rather than assume the
 * singleton is it.
 */
let attachedService: AtlasIframeService | null = null;

class AtlasIframeService {
  private iframe: HTMLIFrameElement | null = null;
  private config: AtlasIframeConfig;
  private messageHandlers: Map<ChildMessageType, MessageHandler[]> = new Map();
  private boundMessageHandler: (event: MessageEvent) => void;

  constructor(config?: Partial<AtlasIframeConfig>) {
    const defaultBaseUrl = process.env.VUE_APP_ATLAS_FRONTEND_URL || 'http://localhost:3000';

    this.config = {
      baseUrl: defaultBaseUrl,
      ...config,
    };

    this.boundMessageHandler = this.handleMessage.bind(this);
  }

  /**
   * Get the allowed origins for message validation
   */
  private get allowedOrigins(): string[] {
    return [
      this.config.baseUrl,
      'http://localhost:3000', // Next.js dev server
      'https://atlas-ui.assisty.ai',
      'https://staging-atlas-ui.assisty.ai',
    ];
  }

  /**
   * Whether an iframe is currently attached. Lets callers outside this module
   * (http.ts's setIdentityToken) ask before pushing a token, rather than
   * reaching into the private field or triggering a spurious warning.
   */
  get isAttached(): boolean {
    return this.iframe !== null;
  }

  /**
   * Initialize the service with an iframe element
   */
  initialize(iframe: HTMLIFrameElement): void {
    this.iframe = iframe;
    // Become the instance outside callers push to. Last mount wins, which is
    // correct: AtlasEmbed is destroyed and recreated on every entry to
    // /assistant/*, so a stale predecessor must not keep the slot.
    attachedService = this;
    window.addEventListener('message', this.boundMessageHandler);

    // Set up default handlers from config
    if (this.config.onReady) {
      this.on('READY', this.config.onReady);
    }
    if (this.config.onAuthRequest) {
      this.on('REQUEST_AUTH', () => {
        this.sendAuthToken();
        this.config.onAuthRequest?.();
      });
    }
    if (this.config.onNavigation) {
      this.on('NAVIGATION', (payload: { route: string }) => {
        this.config.onNavigation?.(payload.route);
      });
    }
    if (this.config.onActionComplete) {
      this.on('ACTION_COMPLETE', (payload: { action: string; result: unknown }) => {
        this.config.onActionComplete?.(payload.action, payload.result);
      });
    }
    if (this.config.onResize) {
      this.on('RESIZE', (payload: { height: number }) => {
        this.config.onResize?.(payload.height);
      });
    }
    if (this.config.onParentNavigate) {
      this.on(
        'PARENT_NAVIGATE',
        (payload: { path: string; switchToClassic?: boolean; reload?: boolean }) => {
          // `reload` (AS-10885): Atlas is asking for a full document load, not
          // a router push, because the merchant's package just changed and
          // Warbler only fetches the store at bootstrap.
          this.config.onParentNavigate?.(payload.path, payload.switchToClassic === true, {
            reload: payload.reload === true,
          });
        },
      );
    }
    if (this.config.onQueryParams) {
      this.on('QUERY_PARAMS', (payload: { queryString: string }) => {
        this.config.onQueryParams?.(payload.queryString);
      });
    }
    if (this.config.onPurchaseListChanged) {
      this.on('PURCHASE_LIST_CHANGED', () => {
        this.config.onPurchaseListChanged?.();
      });
    }
    if (this.config.onNavigateAck) {
      this.on('NAVIGATE_ACK', (payload: { path?: unknown }) => {
        if (payload && typeof payload.path === 'string') {
          this.config.onNavigateAck?.(payload.path);
        }
      });
    }
    if (this.config.onOpenLiveChat) {
      this.on('OPEN_LIVE_CHAT', (payload: unknown) => {
        // postMessage data is untrusted — keep only string fields so nothing
        // non-string can reach Crisp session data.
        const p = (payload ?? {}) as Record<string, unknown>;
        this.config.onOpenLiveChat?.({
          ...(typeof p.ticketKey === 'string' ? { ticketKey: p.ticketKey } : {}),
          ...(typeof p.reason === 'string' ? { reason: p.reason } : {}),
          ...(typeof p.shop === 'string' ? { shop: p.shop } : {}),
          ...(typeof p.message === 'string' ? { message: p.message } : {}),
          ...(typeof p.handoffIntent === 'string' && HANDOFF_INTENTS.includes(p.handoffIntent)
            ? { handoffIntent: p.handoffIntent as HandoffIntent }
            : {}),
          ...(typeof p.summary === 'string' ? { summary: p.summary } : {}),
          ...(typeof p.requestId === 'string' ? { requestId: p.requestId } : {}),
        });
      });
    }
    if (this.config.onReopenLiveChat) {
      this.on('REOPEN_LIVE_CHAT', () => {
        this.config.onReopenLiveChat?.();
      });
    }

    if (this.config.onRedirectExternal) {
      this.on('PARENT_REDIRECT_EXTERNAL', (payload: unknown) => {
        // postMessage data is untrusted, and this one moves the merchant's
        // whole browser. Validate HERE rather than in the callback: warbler is
        // the side that navigates, so it cannot assume Atlas checked first,
        // and a second embed site must not be able to skip the check by
        // supplying its own handler.
        const p = (payload ?? {}) as Record<string, unknown>;
        const url = typeof p.url === 'string' ? p.url : '';
        if (!isAllowedExternalRedirect(url)) {
          console.warn(
            '[AtlasIframeService] refused PARENT_REDIRECT_EXTERNAL to a non-Shopify target:',
            url,
          );
          return;
        }
        this.config.onRedirectExternal?.(url);
      });
    }

    // Always handle PRINT — open print window from parent context (outside iframe sandbox)
    // Clear any existing PRINT handlers to avoid duplicates if initialize() is called again
    this.messageHandlers.delete('PRINT');
    this.on('PRINT', (payload: unknown) => {
      // Validate payload before using
      const p = payload as Record<string, unknown> | null;
      if (!p || typeof p.html !== 'string' || !p.html) {
        console.warn('[AtlasIframeService] PRINT message received with invalid payload', payload);
        return;
      }
      const html = p.html;
      const title = typeof p.title === 'string' ? p.title : 'Purchase Order';

      if (this.config.onPrint) {
        this.config.onPrint(html, title);
      } else {
        // Default print handler — use Blob URL so the content runs in an opaque
        // origin with no access to the parent window (avoids XSS from postMessage)
        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');
        if (printWindow) {
          printWindow.addEventListener('afterprint', () => printWindow.close());
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
          }, 400);
        }
        // Revoke after a delay to allow the window to load
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      }
    });
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    window.removeEventListener('message', this.boundMessageHandler);
    this.messageHandlers.clear();
    this.iframe = null;
    // Only vacate the slot if it is still ours — a newer instance may already
    // have claimed it.
    if (attachedService === this) {
      attachedService = null;
    }
  }

  /**
   * Handle incoming messages from iframe
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin
    if (!this.isValidOrigin(event.origin)) {
      return;
    }

    const message = event.data as ChildMessage;

    // Validate message structure
    if (!message || message.source !== 'atlas-frontend' || !message.type) {
      return;
    }

    // Handle REQUEST_AUTH automatically if no custom handler
    if (message.type === 'REQUEST_AUTH' && !this.messageHandlers.has('REQUEST_AUTH')) {
      this.sendAuthToken();
      return;
    }

    // Dispatch to registered handlers
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => handler(message.payload));
    }
  }

  /**
   * Validate message origin
   */
  private isValidOrigin(origin: string): boolean {
    return this.allowedOrigins.some(
      (allowed) => origin === allowed || origin.endsWith('.assisty.ai')
    );
  }

  /**
   * Register a handler for a message type
   */
  on<T = unknown>(type: ChildMessageType, handler: MessageHandler<T>): () => void {
    const handlers = this.messageHandlers.get(type) || [];
    handlers.push(handler as MessageHandler);
    this.messageHandlers.set(type, handlers);

    // Return unsubscribe function
    return () => {
      const currentHandlers = this.messageHandlers.get(type) || [];
      const index = currentHandlers.indexOf(handler as MessageHandler);
      if (index > -1) {
        currentHandlers.splice(index, 1);
        this.messageHandlers.set(type, currentHandlers);
      }
    };
  }

  /**
   * Send a message to the iframe
   */
  private sendToIframe(type: ParentMessageType, payload: unknown = {}): void {
    if (!this.iframe?.contentWindow) {
      console.warn('[AtlasIframeService] No iframe available');
      return;
    }

    const message: ParentMessage = {
      type,
      payload,
      source: 'warbler-parent',
    };

    this.iframe.contentWindow.postMessage(message, this.config.baseUrl);
  }

  /**
   * Send authentication token to iframe.
   *
   * `token` MUST be supplied when calling from inside setIdentityToken:
   * getIdentityToken() calls setIdentityToken() back (http.ts — it rehydrates
   * the active store's token from token_options whenever window.SHOP_NAME is
   * set, which setIdentityToken itself sets), so re-deriving the token here
   * would recurse until the stack blows. Callers that are not already holding
   * a token (READY, REQUEST_AUTH) can omit it safely.
   */
  async sendAuthToken(token?: string): Promise<void> {
    // No explicit token means a READY / REQUEST_AUTH caller. Reading storage
    // directly would hand Atlas whatever is there — and a MEMBER access token
    // lives only 5 minutes, so that is routinely an already-dead token Atlas
    // has no way to recognise. getFreshIdentityToken refreshes first when, and
    // only when, the token is genuinely expiring; an OWNER/embedded token has
    // no `exp` claim and comes back untouched with no network call. On a failed
    // refresh it resolves the previous token rather than nothing, so Atlas gets
    // a recoverable 401 instead of an empty Authorization header.
    //
    // No recursion risk: getFreshIdentityToken -> getIdentityToken ->
    // setIdentityToken -> sendAuthToken(token) takes the explicit-token branch
    // above and stops there.
    const resolved = token ?? await getFreshIdentityToken();
    if (resolved) {
      this.sendToIframe('AUTH_TOKEN', { token: resolved });
    } else {
      console.warn('[AtlasIframeService] No token available to send');
    }
  }

  /**
   * Ask Atlas to client-side-navigate to an Atlas-relative path, instead of
   * rebuilding the iframe src (which reboots the whole Atlas app).
   *
   * Atlas replies with NAVIGATE_ACK when it accepts the target; callers must
   * treat a missing acknowledgement as "fall back to a hard reload", since an
   * Atlas build without the subscriber would otherwise sit frozen on the old
   * page. See AtlasSoftNavController.
   */
  navigateTo(path: string, query = ''): void {
    this.sendToIframe('NAVIGATE', { path, query });
  }

  /**
   * Sync theme with iframe
   */
  syncTheme(theme: 'light' | 'dark'): void {
    this.sendToIframe('THEME_SYNC', { theme });
  }

  /**
   * Send context data to iframe
   */
  sendContext(data: unknown): void {
    this.sendToIframe('CONTEXT_UPDATE', { data });
  }

  /**
   * Answer an OPEN_LIVE_CHAT (AS-11647 P0-4). Atlas waits for this before
   * telling the merchant they are connected; without it every handoff under
   * warbler was reported as a success, even with no Crisp on the page.
   */
  sendLiveChatResult(requestId: string, success: boolean, message: string): void {
    this.sendToIframe('OPEN_LIVE_CHAT_RESULT', { requestId, success, message });
  }

  /**
   * Mirror the Crisp widget's state into Atlas (AS-11647 P0-5), so Atlas can
   * offer the way back to a handoff and show that the team replied.
   */
  sendLiveChatState(state: LiveChatState): void {
    this.sendToIframe('LIVE_CHAT_STATE', state);
  }

  /**
   * The "Atlas agent" pill: the merchant switched from live chat back to Atlas,
   * so Atlas opens its chat and its toggle leads to Atlas again (AS-11647).
   * An Atlas that predates it ignores the message.
   */
  showAtlas(): void {
    this.sendToIframe('SHOW_ATLAS', {});
  }

  /**
   * Get the iframe source URL for a specific agent
   */
  getIframeSrc(agent?: string): string {
    if (agent) {
      return `${this.config.baseUrl}/${agent}`;
    }
    return this.config.baseUrl;
  }

  /**
   * Update the configuration
   */
  updateConfig(config: Partial<AtlasIframeConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

// Export a factory function for creating service instances
export function createAtlasIframeService(config?: Partial<AtlasIframeConfig>): AtlasIframeService {
  return new AtlasIframeService(config);
}

/**
 * The service bound to a live iframe right now, or null when Atlas is not
 * mounted. Use this — not the singleton below — to reach the real iframe from
 * outside AtlasEmbed.
 */
export function getAttachedAtlasIframeService(): AtlasIframeService | null {
  return attachedService;
}

// Export a singleton for simple use cases
export const atlasIframeService = new AtlasIframeService();

export default atlasIframeService;
